<script setup>
import { computed, ref, watch } from 'vue'
import { userAuthState } from '@/@core/auth'
import { useDashboardResource } from '@/composables/useDashboardResource'
import { PREFERENCE_DASHBOARD_TABS, usePreferenceDashboardFilters } from '@/composables/usePreferenceDashboardFilters'
import preferenceDashboardService from '@/services/feedback/preferenceDashboard.service'
import PreferenceAdmissionCoverage from '@/views/feedback/dashboard/preference/PreferenceAdmissionCoverage.vue'
import PreferenceAdmittedKpis from '@/views/feedback/dashboard/preference/PreferenceAdmittedKpis.vue'
import PreferenceAiSuggestions from '@/views/feedback/dashboard/preference/PreferenceAiSuggestions.vue'
import PreferenceBacklogAging from '@/views/feedback/dashboard/preference/PreferenceBacklogAging.vue'
import PreferenceCapturedByDepartment from '@/views/feedback/dashboard/preference/PreferenceCapturedByDepartment.vue'
import PreferenceConfigWarning from '@/views/feedback/dashboard/preference/PreferenceConfigWarning.vue'
import PreferenceDashboardFilterBar from '@/views/feedback/dashboard/preference/PreferenceDashboardFilterBar.vue'
import PreferenceHistoryKpis from '@/views/feedback/dashboard/preference/PreferenceHistoryKpis.vue'
import PreferenceListDialog from '@/views/feedback/dashboard/preference/PreferenceListDialog.vue'
import PreferenceMonthlyNew from '@/views/feedback/dashboard/preference/PreferenceMonthlyNew.vue'
import PreferencePriorityCards from '@/views/feedback/dashboard/preference/PreferencePriorityCards.vue'
import PreferenceTimeToAcknowledge from '@/views/feedback/dashboard/preference/PreferenceTimeToAcknowledge.vue'
import PreferenceUnackByDepartment from '@/views/feedback/dashboard/preference/PreferenceUnackByDepartment.vue'

definePage({
  meta: {
    name: 'dashboard-preference',
    subject: 'feedback.menu',
    action: 'dashboard_preference',
  },
})

const { t } = useI18n()
const auth = userAuthState()

const {
  tab,
  unitId,
  dateRange,
  datePickerConfig,
  debouncedFilters,
} = usePreferenceDashboardFilters()

const unitName = computed(() => auth.user?.units?.find(u => u.id === unitId)?.name ?? '')

// The admitted summary feeds the KPI strip and the priority cards, so the
// page loads it once. Point-in-time: only the unit matters, and the unit
// never changes without a page reload.
const admittedSummary = useDashboardResource(
  () => preferenceDashboardService.admittedSummary(debouncedFilters.value),
  { errorMessage: 'Could not load the admitted summary' },
)

watch(() => debouncedFilters.value.unitId, id => {
  if (id)
    admittedSummary.load()
}, { immediate: true })

// The list dialog behind a priority card.
const listOpen = ref(false)
const listPriority = ref(null)

const listCounts = computed(() => admittedSummary.data.value?.by_priority?.[listPriority.value?.toLowerCase()] ?? { total: 0, unacknowledged: 0 })

const openList = priority => {
  listPriority.value = priority
  listOpen.value = true
}
</script>

<template>
  <div>
    <div class="d-flex flex-column flex-md-row justify-space-between align-stretch align-md-center gap-4 mb-6">
      <div>
        <h1 class="text-h4 font-weight-bold">
          Preference Dashboard
        </h1>
        <p class="text-body-1 text-medium-emphasis mb-0">
          Are {{ t('customer') }} preferences being captured and acknowledged
          <template v-if="unitName">
            in {{ unitName }}
          </template>
        </p>
      </div>

      <PreferenceDashboardFilterBar
        v-model:tab="tab"
        v-model:date-range="dateRange"
        :tabs="PREFERENCE_DASHBOARD_TABS"
        :date-picker-config="datePickerConfig"
      />
    </div>

    <VAlert
      v-if="!unitId"
      type="warning"
      variant="tonal"
    >
      Select a unit from the top bar to see its preference dashboard.
    </VAlert>

    <!--
      VWindowItem is lazy by default: the hidden tab's widgets are not
      mounted, so they do not fetch until the tab is first opened.
    -->
    <VWindow
      v-else
      v-model="tab"
      :touch="false"
    >
      <VWindowItem value="admitted">
        <VRow>
          <PreferenceConfigWarning :filters="debouncedFilters" />
          <VCol cols="12">
            <PreferenceAdmittedKpis
              :summary="admittedSummary.data.value"
              :loading="admittedSummary.isLoading.value"
              :error="admittedSummary.error.value"
              @retry="admittedSummary.load"
            />
          </VCol>
          <VCol cols="12">
            <PreferencePriorityCards
              :summary="admittedSummary.data.value"
              :loading="admittedSummary.isLoading.value"
              :error="admittedSummary.error.value"
              @retry="admittedSummary.load"
              @open="openList"
            />
          </VCol>
          <VCol cols="12">
            <PreferenceBacklogAging :filters="debouncedFilters" />
          </VCol>
          <VCol cols="12">
            <PreferenceUnackByDepartment :filters="debouncedFilters" />
          </VCol>
        </VRow>
      </VWindowItem>

      <VWindowItem value="history">
        <VRow>
          <VCol cols="12">
            <PreferenceHistoryKpis :filters="debouncedFilters" />
          </VCol>
          <VCol
            cols="12"
            md="6"
          >
            <PreferenceAdmissionCoverage :filters="debouncedFilters" />
          </VCol>
          <VCol
            cols="12"
            md="6"
          >
            <PreferenceMonthlyNew :filters="debouncedFilters" />
          </VCol>
          <VCol cols="12">
            <PreferenceCapturedByDepartment :filters="debouncedFilters" />
          </VCol>
          <VCol cols="12">
            <PreferenceTimeToAcknowledge :filters="debouncedFilters" />
          </VCol>
          <PreferenceAiSuggestions :filters="debouncedFilters" />
        </VRow>
      </VWindowItem>
    </VWindow>

    <PreferenceListDialog
      v-if="unitId"
      v-model:is-dialog-open="listOpen"
      :priority="listPriority"
      :counts="listCounts"
      :filters="debouncedFilters"
    />
  </div>
</template>
